import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Search, Tags, BookOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import TopBar from '../../components/TopBar.jsx';
import { apiGet, apiPost, apiDel } from '../../lib/api.js';

const SOURCES = [
  'script',
  'objection',
  'carrier',
  'compliance',
  'training_call',
  'winning_close',
  'note',
  'custom',
];

export default function KnowledgeBase() {
  const qc = useQueryClient();

  const [sourceFilter, setSourceFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState(null); // null = list mode, [] = search mode

  const [form, setForm] = useState({ source: 'script', title: '', content: '', tags: '' });
  const [showForm, setShowForm] = useState(false);

  const listParams = useMemo(() => {
    const p = {};
    if (sourceFilter) p.source = sourceFilter;
    if (tagFilter) p.tags = tagFilter;
    p.limit = 100;
    return p;
  }, [sourceFilter, tagFilter]);

  const listQuery = useQuery({
    queryKey: ['knowledge:chunks', listParams],
    queryFn: () => apiGet('/knowledge/chunks', listParams),
  });

  const addMutation = useMutation({
    mutationFn: (body) => apiPost('/knowledge/chunks', body),
    onSuccess: () => {
      toast.success('Chunk added');
      setForm({ source: 'script', title: '', content: '', tags: '' });
      setShowForm(false);
      qc.invalidateQueries({ queryKey: ['knowledge:chunks'] });
    },
    onError: (e) => toast.error(e.message || 'Failed to add chunk'),
  });

  const delMutation = useMutation({
    mutationFn: (id) => apiDel(`/knowledge/chunks/${id}`),
    onSuccess: () => {
      toast.success('Deleted');
      qc.invalidateQueries({ queryKey: ['knowledge:chunks'] });
    },
    onError: (e) => toast.error(e.message || 'Failed to delete'),
  });

  const searchMutation = useMutation({
    mutationFn: (query) =>
      apiPost('/knowledge/search', {
        query,
        k: 20,
        filters: { source: sourceFilter || undefined, tags: tagFilter ? tagFilter.split(',').map(s => s.trim()) : undefined },
      }),
    onSuccess: (res) => setSearchResults(res?.data || []),
    onError: (e) => {
      toast.error(e.message || 'Search failed');
      setSearchResults(null);
    },
  });

  function onSearch(e) {
    e.preventDefault();
    if (!searchText.trim()) {
      setSearchResults(null);
      return;
    }
    searchMutation.mutate(searchText.trim());
  }

  function onSubmitAdd(e) {
    e.preventDefault();
    if (!form.content.trim()) {
      toast.error('Content is required');
      return;
    }
    addMutation.mutate({
      source:  form.source,
      title:   form.title || '',
      content: form.content,
      tags:    form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
    });
  }

  const items = searchResults ?? (listQuery.data?.data || []);
  const isSearchMode = Array.isArray(searchResults);

  return (
    <div className="space-y-4">
      <TopBar
        title="Knowledge Base"
        subtitle="Per-tenant cognitive layer — scripts, objections, winning closes feed the AI brain"
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 text-sm text-white/60">
          <BookOpen className="w-4 h-4 text-civix-purple" />
          {isSearchMode
            ? `${items.length} semantic search hits`
            : `${listQuery.data?.total ?? items.length} chunks`}
        </div>
        <div className="grow" />
        <button
          onClick={() => setShowForm((s) => !s)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add chunk
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={onSubmitAdd}
          className="bg-white/5 rounded-xl p-4 border border-white/10 space-y-3"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="text-sm">
              <div className="text-white/60 mb-1">Source</div>
              <select
                className="input"
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
              >
                {SOURCES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
            <label className="text-sm md:col-span-2">
              <div className="text-white/60 mb-1">Title (optional)</div>
              <input
                className="input w-full"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Spouse objection rebuttal"
              />
            </label>
          </div>
          <label className="text-sm block">
            <div className="text-white/60 mb-1">Content</div>
            <textarea
              className="input w-full min-h-[140px]"
              maxLength={8000}
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder="The chunk text — what the AI should learn from."
            />
          </label>
          <label className="text-sm block">
            <div className="text-white/60 mb-1">Tags (comma-separated)</div>
            <input
              className="input w-full"
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              placeholder="spouse, stall, rebuttal"
            />
          </label>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={addMutation.isPending}>
              {addMutation.isPending ? 'Saving…' : 'Save chunk'}
            </button>
          </div>
        </form>
      )}

      <div className="bg-white/5 rounded-xl p-4 border border-white/10 space-y-3">
        <form onSubmit={onSearch} className="flex flex-wrap items-end gap-2">
          <label className="text-sm grow">
            <div className="text-white/60 mb-1 flex items-center gap-1">
              <Search className="w-3 h-3" /> Semantic search
            </div>
            <input
              className="input w-full"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Ask the knowledge base — e.g. 'they want to think about it'"
            />
          </label>
          <label className="text-sm">
            <div className="text-white/60 mb-1">Source</div>
            <select
              className="input"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
            >
              <option value="">All</option>
              {SOURCES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <div className="text-white/60 mb-1 flex items-center gap-1">
              <Tags className="w-3 h-3" /> Tags
            </div>
            <input
              className="input"
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              placeholder="comma,separated"
            />
          </label>
          <button type="submit" className="btn-primary" disabled={searchMutation.isPending}>
            {searchMutation.isPending ? 'Searching…' : 'Search'}
          </button>
          {isSearchMode && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => { setSearchResults(null); setSearchText(''); }}
            >
              Clear
            </button>
          )}
        </form>
      </div>

      <div className="space-y-2">
        {listQuery.isLoading && !isSearchMode ? (
          <div className="text-white/50 text-sm p-4">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-white/50 text-sm p-4 bg-white/5 rounded-xl border border-white/10">
            No chunks yet. Click <span className="text-white">Add chunk</span> to seed knowledge.
          </div>
        ) : (
          items.map((c) => (
            <div
              key={c._id}
              className="bg-white/5 rounded-xl p-4 border border-white/10 flex gap-3"
            >
              <div className="grow min-w-0">
                <div className="flex items-center gap-2 text-xs text-white/60">
                  <span className="px-2 py-0.5 rounded-sm bg-civix-purple/30 text-white">
                    {c.source}
                  </span>
                  {c.title ? <span className="text-white/80">{c.title}</span> : null}
                  {typeof c.score === 'number' ? (
                    <span className="ml-auto text-white/50">score {c.score.toFixed(3)}</span>
                  ) : null}
                </div>
                <div className="mt-2 text-sm text-white/90 whitespace-pre-wrap wrap-break-word">
                  {c.content}
                </div>
                {Array.isArray(c.tags) && c.tags.length ? (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {c.tags.map((t) => (
                      <span key={t} className="text-xs px-2 py-0.5 rounded-sm bg-white/10 text-white/70">
                        {t}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
              <button
                onClick={() => { if (confirm('Delete this chunk?')) delMutation.mutate(c._id); }}
                className="shrink-0 text-white/60 hover:text-red-400"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
